/**
 * Utility functions to check server status before connecting to Socket.IO
 */

/**
 * Checks if the server is running by making a fetch request to the status endpoint
 * @returns {Promise<{ok: boolean, message: string}>} Result of the server check
 */
export const checkServerStatus = async () => {
  try {
    // Try to detect if we're on a mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    console.log('Device detection for server check:', { isMobile, userAgent: navigator.userAgent });
    
    // Try to fetch the server status with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), isMobile ? 30000 : 20000);
    
    // ALWAYS use the Cloudflare tunnel URL - never use localhost
    const serverUrl = 'https://gibberers-server.16022001.xyz';
    
    console.log('Checking server status at:', serverUrl);
    
    // Try multiple endpoints with different fetch options
    let response = null;
    let error = null;
    
    // For Cloudflare tunnel, we need to try multiple approaches
    const fetchOptions = [
      // Option 1: Root endpoint
      {
        url: `${serverUrl}`,
        options: {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors',
          cache: 'no-cache'
        },
        name: 'Root endpoint'
      },
      // Option 2: Status endpoint
      {
        url: `${serverUrl}/api/status`,
        options: {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Accept': 'application/json'
          }
        },
        name: 'Status endpoint'
      },
      // Option 3: Ping endpoint
      {
        url: `${serverUrl}/api/ping`,
        options: {
          method: 'GET',
          signal: controller.signal,
          mode: 'cors',
          cache: 'no-cache'
        },
        name: 'Ping endpoint'
      }
    ];
    
    // Try each option in sequence until one works
    for (const fetchOption of fetchOptions) {
      try {
        console.log(`Trying ${fetchOption.name}`);
        response = await fetch(fetchOption.url, fetchOption.options);
        console.log(`${fetchOption.name} response:`, response);
        
        if (response) {
          console.log(`${fetchOption.name} succeeded`);
          break; // Exit the loop if we got a response
        }
      } catch (e) {
        console.log(`${fetchOption.name} failed:`, e);
        error = e;
        // Continue to the next option
      }
    }
    
    clearTimeout(timeoutId);
    
    // If we got any response, consider it a success
    if (response) {
      return { ok: true, message: 'Server is running' };
    } else {
      // If all options failed, throw the last error
      throw error || new Error('No response from server after trying all options');
    }
  } catch (error) {
    console.error('Server check error:', error);
    if (error.name === 'AbortError') {
      throw new Error('Server connection timed out');
    }
    throw new Error(`Server check failed: ${error.message}`);
  }
};

/**
 * Simplified version that just returns a boolean
 * @returns {Promise<boolean>} Whether the server is running
 */
export const checkServer = async () => {
  try {
    await checkServerStatus();
    return true;
  } catch (error) {
    console.error('Server check failed:', error);
    return false;
  }
};

/**
 * Fallback method that assumes the server is running
 * Use this when the regular check fails but you want to try connecting anyway
 */
export const assumeServerRunning = () => {
  console.log('Assuming server is running without checking');
  return Promise.resolve({ ok: true, message: 'Assuming server is running' });
}; 